// CAUTION
//
// Google Analytics Paid Media Tracking
// These routes are utilized by GA.
//
import { ActivityTypeGroups, MedicalNoteType } from '@uplift/api';
import { AvailabilitySettingsModalType, CalendarEditView } from '@uplift/types';

// -- !!! Please inform the team of your changes :) !!! --
import { createTypedDynamicRoute } from './create-typed-dynamic-route';

export const patientIntakeFormRoute = createTypedDynamicRoute<{
  patientId: string;
  submissionId: string;
}>(params => `/clients/${params.patientId}/intake-form/${params.submissionId}`);

export const providerScheduleViewTypeRoute = createTypedDynamicRoute<
  Record<string, never>,
  {
    editView?: CalendarEditView;
    createAppointment?: 'add' | 'invite';
    modal?: AvailabilitySettingsModalType;
  }
>(_params => '/schedule');

export const providerSubmitSessionRoute = createTypedDynamicRoute<
  { appointmentId: string },
  { redirect_to?: string }
>((params) => `/appointments/${params.appointmentId}/submit-session`);

export const findPsychiatryRoute = createTypedDynamicRoute<{}, { source?: string }>(() => '/find-psychiatrist');

export const providerPatientRoute = createTypedDynamicRoute<
  {
    patientId: string;
  },
  {
    activityType?: ActivityTypeGroups;
    activityId?: string;
  }
>(params => `/clients/${params.patientId}`);

export const careTeamProviderRoute = createTypedDynamicRoute<{ id: string }>(
  params => `/care-team/${params.id}`
);

export const appointmentSessionRoute = createTypedDynamicRoute<{
  appointmentId: string;
}>(params => `/appointments/${params.appointmentId}/session`);

export const notesRoute = createTypedDynamicRoute<{
  patientId: string;
  noteType: MedicalNoteType;
  noteId?: string;
}>(params => `/clients/${params.patientId}/note/${params.noteType}/${params.noteId ?? ''}`);

export const claimCorrections = createTypedDynamicRoute<{
  patientId: string;
}>(params => `/clients/${params.patientId}/claim-corrections`);

export const adminUserProfile = createTypedDynamicRoute<{
  userId: string;
  // tab: 'details' | 'credentialing' | 'availability' | 'activity';
}>(params => `/profile/${params.userId}`);

export const adminUserProfileDetails = createTypedDynamicRoute<{ userId: string }>(params => `/profile/${params.userId}/details`);
export const adminUserProfileCredentialing = createTypedDynamicRoute<{ userId: string }>(params => `/profile/${params.userId}/credentialing`);
export const adminUserProfileAvailability = createTypedDynamicRoute<{ userId: string }>(params => `/profile/${params.userId}/availability`);
export const adminUserProfileActivity = createTypedDynamicRoute<{ userId: string }>(params => `/profile/${params.userId}/activity`);

export const adminPatientAppointments = createTypedDynamicRoute<{
  userId: string;
}>(params => `/profile/${params.userId}/patient-appointments`);

export const onboardingError = createTypedDynamicRoute<
  Record<string, never>,
  { error?: 'invalid_invite' }
>(() => '/onboarding/error');

export const commonError = createTypedDynamicRoute<
  Record<string, never>,
  { error?: string }
>(() => '/error');

export const paymentsTabRoute = createTypedDynamicRoute<
  { tab: 'overview' | 'method' },
  { payBalance?: string }
>(params => `/payments/${params.tab}`);

export const higherEducationProfile = createTypedDynamicRoute<{
  id: string;
}>(params => `/higher-education-profile/${params.id}`);

export const higherEducationProfileDetails = createTypedDynamicRoute<{ id: string }>(params => `/higher-education-profile/${params.id}/details`);

export const higherEducationManagerProfile = createTypedDynamicRoute<{
  managerId: string;
}>(params => `/higher-education/manager-profile/${params.managerId}`);

export const higherEducationManagerProfileDetails = createTypedDynamicRoute<{ managerId: string }>(params => `/higher-education/manager-profile/${params.managerId}/details`);
export const settingsRoute = createTypedDynamicRoute<{}, { edit: 'emergencyContact' }>(() => '/settings');

export const clientScheduleSessionRoute = createTypedDynamicRoute<{
  providerId: string;
}>(params => `/schedule-session/${params.providerId}`);

export const ROUTING_CONFIG = {
  home: '/',
  reports: '/reports/upcoming',

  providerMatchingFieldsReport: '/reports/provider_matching_fields',
  providerLicenses: '/reports/provider_licenses',
  providerLicensesExpiring: '/reports/provider_licenses_expiring',
  providerLicensesExpired: '/reports/provider_licenses_expired',
  credentialingLicenseStatus: '/reports/credentialing/license_status',
  credentialingCredentialingStatus: '/reports/credentialing/credentialing_statuses',
  clientMultipleSeries: '/reports/client_multiple_series',
  providerUnsubmittedSessions: '/reports/provider_unsubmitted_sessions',
  providerCancelledSessions: '/reports/provider_cancelled_sessions',
  clientOutstandingBalance: '/reports/client_outstanding_balance',
  clientClaimsSubmission: '/reports/claims_submission',
  reportsPast: '/reports/past',

  matching: '/matching',

  // Admin Pages
  admins: '/admins',
  adminUserProfile: adminUserProfile.template(),
  adminUserProfileDetails: adminUserProfileDetails.template(),
  adminUserProfileCredentialing: adminUserProfileCredentialing.template(),
  adminUserProfileAvailability: adminUserProfileAvailability.template(),
  adminUserProfileActivity: adminUserProfileActivity.template(),
  adminPatientAppointments: adminPatientAppointments.template(),
  adminMatchingView: '/matching-view',

  upload: '/upload/',
  utils: '/utils',
  providersActivation: '/providers-activation',
  rostering: '/rostering',
  messaging: '/messaging',
  messagingReport: '/messaging-report',
  insuranceMarkets: '/insurance-markets',
  verifiableLicenseMapping: '/verifiable-license-mapping',

  // Higher Education Admin
  adminHigherEducation: '/higher-education',
  higherEducationNew: '/higher-education/new',
  higherEducationProfile: higherEducationProfile.template(),
  higherEducationProfileDetails: higherEducationProfileDetails.template(),
  higherEducationManagerProfile: higherEducationManagerProfile.template(),
  higherEducationManagerProfileDetails: higherEducationManagerProfileDetails.template(),

  // Care App
  organizationSettings: '/organization-settings',

  createAccount: '/create-account',
  aboutYou: '/about-you',
  inviteInvalid: '/invite-invalid',
  commonError: commonError.template(),

  //
  login: '/auth/login',
  registration: '/auth/registration',
  resetPassword: '/auth/reset-password',
  setPassword: '/auth/set-password',
  main: '/main',
  payments: '/payments',
  insurance: '/insurance',
  paymentsTab: paymentsTabRoute.template(),
  schedule: providerScheduleViewTypeRoute.template(),
  finance: '/finance',
  financeMonth: '/finance/:date/:filter',
  support: '/support',
  any: '*',
  appointmentsSession: appointmentSessionRoute.template(),
  appointmentsSubmitSession: providerSubmitSessionRoute.template(),
  patients: '/clients',
  patient: providerPatientRoute.template(),
  patientIntakeForm: patientIntakeFormRoute.template(),
  notSubmittedSessions: '/notsubmitted-sessions',
  pageNotFound: '/404',
  profile: '/profile',
  careTeam: '/care-team',
  careTeamProfile: careTeamProviderRoute.template(),
  providerProfile: '/profile/:id',
  rematching: '/rematching/:id',
  deeplinkRematching: '/rematching',
  settings: settingsRoute.template(),
  billing: '/settings/billing',
  dashboard: '/dashboard',
  termsOfService: '/terms-of-service',
  privacyPolicy: '/privacy-policy',
  notes: notesRoute.template(),
  claimCorrections: claimCorrections.template(),

  // provider onboarding
  invitation: '/invitation',
  invitationInsurance: '/invitation/insurance',
  invitationPayment: '/invitation/payment',
  onboardingWelcome: '/onboarding/welcome',
  onboardingLoginInfo: '/onboarding/login-info',
  onboardingInvite: '/onboarding/invite',
  onboardingGeneral: '/onboarding/general',
  onboardingPractice: '/onboarding/practice',
  onboardingClinical: '/onboarding/clinical',
  onboardingAvailability: '/onboarding/availability',
  onboardingFinance: '/onboarding/finance',
  onboardingComplete: '/onboarding/complete',
  onboardingError: onboardingError.template(),

  legacyOnboard: '/onboard',
  legacyOnboardConnect: '/onboard/connect',

  // form builder
  allForms: '/forms',
  importForm: '/form/import',
  formTags: '/form/tags',
  newFormTag: '/form/tags/:formTagId',
  form: '/form/:formId',
  previewForm: '/form/:formId/preview/:page',
  formElement: '/form/:formId/element/:elementId',
  newFormElement: '/form/:formId/element/:elementId/:type',
  formGroup: '/form/:formId/group/:formGroupId',
  submissionsList: '/form/:formId/submissions',
  mappings: '/formMappings',

  // form submit
  formBySubmission: '/form/submission/:submissionId',
  formStart: '/form/:submissionId/start', // reserving for use
  formFinish: '/form/:submissionId/finish', // reserving for use
  formPage: '/form/:submissionId/page/:page',

  featureFlags: '/featureFlags',

  nextAssessment: '/nextAssessment',

  findPsychiatrist: findPsychiatryRoute.template(),

  publicProviderProfile: '/provider/:medicalRole/:providerData',

  profileUrlList: '/profile-urls',
  clientScheduleSession: clientScheduleSessionRoute.template(),
};

export const PATIENT_ONBOARDING_ROUTING_CONFIG = {
  onboarding: '/onboarding',
  welcome: '/onboarding/welcome',
  where: '/onboarding/where',
  where1: '/onboarding/where1',
  notAvailable: '/onboarding/not-available',
  insurance: '/onboarding/insurance',
  who: '/onboarding/who',
  reason: '/onboarding/reason',
  age: '/onboarding/age',
  tooYoung: '/onboarding/too-young',
  providerFilters: '/onboarding/provider-filters',
  providerIdentityFilter: '/onboarding/provider-identity-filter',
  providerExperienceFilter: '/onboarding/provider-experience-filter',
  levelOfCare: '/onboarding/level-of-care',
  assessmentEntry: '/onboarding/assessment',
  assessmentQuestion1: '/onboarding/assessment/phq2/1',
  assessmentQuestion2: '/onboarding/assessment/phq2/2',
  assessmentQuestion3: '/onboarding/assessment/gad2/1',
  assessmentQuestion4: '/onboarding/assessment/gad2/2',
  assessmentMedHistory: '/onboarding/assessment/med-history',
  assessmentHasTherapist: '/onboarding/assessment/has-therapist',
  assessmentIsTherapistGoodFit: '/onboarding/assessment/is-therapist-good-fit',
  assessmentResult: '/onboarding/assessment/result',
  createAccount: '/onboarding/create-account',
  matchesLoading: '/onboarding/matches-loading',
  matches: '/onboarding/matches',
  match: '/onboarding/match',
  aboutYou: '/onboarding/about-you',
  insuranceInfo: '/onboarding/insurance-info',
  paymentInfo: '/onboarding/payment-info',
  confirmBooking: '/onboarding/confirm-booking',
  bookingSuccess: '/onboarding/booking-success',
  psychInterstitial: '/onboarding/booking-both-psych-interstitial',
  bookingBothPsychMatches: '/onboarding/booking-both-psych-matches',
  bookingBothPsychMatch: '/onboarding/booking-both-psych-match',
  bookingBothPsychInsurance: '/onboarding/booking-both-psych-insurance',
} as const;
export const PATIENT_ONBOARDING_ROUTES: string[] = Object.values(PATIENT_ONBOARDING_ROUTING_CONFIG);

export const STUDENT_ONBOARDING_ROUTING_CONFIG = {
  onboarding: '/student/onboarding',
  invalidLink: '/student/onboarding/invalid-link',
  welcome: '/student/onboarding/welcome',
  createAccount: '/student/onboarding/create-account',
  verifyPhone: '/student/onboarding/verify-phone',
  verifyPhoneCode: '/student/onboarding/verify-phone-code',
  where: '/student/onboarding/where',
  notAvailable: '/student/onboarding/not-available',
  insurance: '/student/onboarding/insurance',
  age: '/student/onboarding/age',
  tooYoung: '/student/onboarding/too-young',
  reason: '/student/onboarding/reason',
  providerFilters: '/student/onboarding/provider-filters',
  providerIdentityFilter: '/student/onboarding/provider-identity-filter',
  providerExperienceFilter: '/student/onboarding/provider-experience-filter',
  matchesLoading: '/student/onboarding/matches-loading',
  matches: '/student/onboarding/matches',
  match: '/student/onboarding/match',
  aboutYou: '/student/onboarding/about-you',
  insuranceInfo: '/student/onboarding/insurance-info',
  paymentInfo: '/student/onboarding/payment-info',
  confirmBooking: '/student/onboarding/confirm-booking',
  bookingSuccess: '/student/onboarding/booking-success',
  bookingFirstInterstitial: '/student/onboarding/booking-first-interstitial',
  bookingSecondInterstitial: '/student/onboarding/booking-second-interstitial',
  secondMatches: '/student/onboarding/second-matches',
  secondMatch: '/student/onboarding/second-match',
} as const;
export const STUDENT_ONBOARDING_ROUTES: string[] = Object.values(STUDENT_ONBOARDING_ROUTING_CONFIG);


export const PROVIDER_ONBOARDING_ROUTES = [
  ROUTING_CONFIG.onboardingWelcome,
  ROUTING_CONFIG.onboardingLoginInfo,
  ROUTING_CONFIG.onboardingInvite,
  ROUTING_CONFIG.onboardingGeneral,
  ROUTING_CONFIG.onboardingPractice,
  ROUTING_CONFIG.onboardingClinical,
  ROUTING_CONFIG.onboardingFinance,
  ROUTING_CONFIG.onboardingComplete,
  ROUTING_CONFIG.onboardingError
];

export const CLIENT_FORM_ROUTES = [
  ROUTING_CONFIG.formPage,
  ROUTING_CONFIG.formStart,
  ROUTING_CONFIG.formFinish,
];

const matchSessionRoutesRegex = /\/appointments\/.+\/session/g;

export const routeIsSession = (pathname = '') => {
  return (pathname.match(matchSessionRoutesRegex)?.length ?? 0) > 0;
};
